type PalColor = "yellow" | "blue" | "purple" | "orange" | "green" | "pink";
type PalMood = "happy" | "wave" | "listen" | "cheer" | "calm";

const colorMap: Record<PalColor, string> = {
  yellow: "var(--pal-yellow)",
  blue: "var(--pal-blue)",
  purple: "var(--pal-purple)",
  orange: "var(--pal-orange)",
  green: "var(--pal-green)",
  pink: "var(--pal-pink)",
};

interface Props {
  color?: PalColor;
  mood?: PalMood;
  size?: number;
  className?: string;
  /** Mascot evolution stage 0..4 — gated at 5/15/30/50 mastered microskills. */
  stage?: 0 | 1 | 2 | 3 | 4;
}

/** Pencil mascot — wood tip, painted body, little face. */
export function Pal({ color = "blue", mood = "happy", size = 96, className = "", stage = 0 }: Props) {
  const fill = colorMap[color];
  const eyeY = mood === "cheer" ? 78 : 80;
  const mouth =
    mood === "cheer"
      ? "M62 92 Q72 104 82 92"
      : mood === "listen"
        ? "M64 94 Q72 98 80 94"
        : mood === "calm"
          ? "M64 93 H80"
          : "M62 90 Q72 100 82 90";
  return (
    <svg
      viewBox="0 0 144 200"
      width={size}
      height={(size * 200) / 144}
      className={className}
      aria-hidden
      data-pal-stage={stage}
      data-pal-color={color}
    >
      {/* shadow */}
      <ellipse cx="72" cy="190" rx="34" ry="5" fill="rgb(58 38 18 / 0.18)" />
      {/* body */}
      <rect x="28" y="40" width="88" height="130" rx="14" fill={fill} />
      {/* highlight */}
      <rect x="34" y="46" width="10" height="118" rx="5" fill="white" opacity="0.25" />
      {/* metal ferrule */}
      <rect x="28" y="32" width="88" height="14" rx="4" fill="#d8c79a" />
      <rect x="28" y="32" width="88" height="3" fill="#bda673" />
      <rect x="28" y="42" width="88" height="2" fill="#bda673" />
      {/* eraser */}
      <path d="M30 32 Q30 14 72 14 Q114 14 114 32 Z" fill="#e89aa3" />
      {/* wood tip */}
      <path d="M28 170 L72 196 L116 170 Z" fill="#e8cf9f" />
      {/* graphite */}
      <path d="M58 182 L72 196 L86 182 Z" fill="#2a241d" />
      {/* face */}
      <circle cx="60" cy={eyeY} r="3.6" fill="#1F1A14" />
      <circle cx="84" cy={eyeY} r="3.6" fill="#1F1A14" />
      <circle cx="61.2" cy={eyeY - 1.2} r="1.1" fill="white" />
      <circle cx="85.2" cy={eyeY - 1.2} r="1.1" fill="white" />
      {/* cheeks */}
      <circle cx="52" cy="92" r="4" fill="rgb(232 154 163 / 0.7)" />
      <circle cx="92" cy="92" r="4" fill="rgb(232 154 163 / 0.7)" />
      {/* mouth */}
      <path d={mouth} stroke="#1F1A14" strokeWidth="2.6" strokeLinecap="round" fill="none" />
      {/* arm waving */}
      {mood === "wave" && (
        <g className="origin-bottom">
          <path
            d="M116 96 Q138 80 132 60"
            stroke={fill}
            strokeWidth="10"
            strokeLinecap="round"
            fill="none"
          />
          <circle cx="132" cy="58" r="7" fill={fill} />
        </g>
      )}
      {mood === "cheer" && (
        <>
          <path d="M28 90 Q10 70 18 52" stroke={fill} strokeWidth="10" strokeLinecap="round" fill="none" />
          <path d="M116 90 Q134 70 126 52" stroke={fill} strokeWidth="10" strokeLinecap="round" fill="none" />
        </>
      )}

      {/* Evolution overlays — stack additively. Drawn over body so they show through cheer arms. */}
      {/* Stage 1 (5+ mastered): little leaf badge on the body */}
      {stage >= 1 && (
        <g>
          <circle cx="42" cy="58" r="7" fill="#5a8a4a" />
          <path d="M38 58 Q42 52 46 58 Q42 60 38 58 Z" fill="#7fb068" />
        </g>
      )}
      {/* Stage 2 (15+): crown of stars */}
      {stage >= 2 && (
        <g fill="#f4c95d" stroke="#3a2612" strokeWidth="0.6">
          <path d="M58 22 l2 4 4 .6 -3 3 .8 4.4 -3.8-2.2 -3.8 2.2 .8-4.4 -3-3 4-.6 z" />
          <path d="M86 22 l2 4 4 .6 -3 3 .8 4.4 -3.8-2.2 -3.8 2.2 .8-4.4 -3-3 4-.6 z" />
        </g>
      )}
      {/* Stage 3 (30+): glow halo */}
      {stage >= 3 && (
        <circle cx="72" cy="100" r="68" fill="none" stroke={fill} strokeWidth="2" opacity="0.35" strokeDasharray="3 5" />
      )}
      {/* Stage 4 (50+): sparkle aura */}
      {stage >= 4 && (
        <g fill="#f4c95d">
          <circle cx="20" cy="60" r="2" />
          <circle cx="124" cy="80" r="2.5" />
          <circle cx="14" cy="120" r="1.8" />
          <circle cx="130" cy="140" r="2" />
          <circle cx="24" cy="160" r="1.6" />
        </g>
      )}
    </svg>
  );
}

export const PAL_NAMES: { color: PalColor; name: string; tagline: string }[] = [
  { color: "yellow", name: "Pip", tagline: "Friendly. Loves a good warm-up." },
  { color: "blue", name: "Brook", tagline: "Calm. Likes counting." },
  { color: "purple", name: "Berry", tagline: "Curious. A word collector." },
  { color: "orange", name: "Sunny", tagline: "Bright. Cheers you on." },
  { color: "green", name: "Sprout", tagline: "Patient. Grows with you." },
  { color: "pink", name: "Pippa", tagline: "Playful. Loves a good story." },
];
export type { PalColor };
